import { useAttachmentsUploadManager } from '@components'

import { useModal } from '@billy/design-system'
import noop from 'lodash/noop'
import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { Contact } from '@features/contacts/types/contact'
import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { SendAsEInvoiceForm } from '@features/invoices/types/sendAsEInvoiceForm'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { useForm, UseFormReturn } from '../../../../../hooks'
import { useUpdateCustomerInformation } from '../../../hooks/useUpdateCustomerInformation'
import type { PredefinedCustomerValues } from '../../../types/predefinedCustomerValues'
import { AddressingLineLookupStatus } from '../enums/addressingLineLookupStatus'
import { useFetchEInvoicingAddressingLines } from '../hooks/useFetchEInvoicingAddressingLines'
import { EInvoicingAddressFormSchema } from '../types/eInvoicingAddressFormSchema'
import { getEInvoicingAddressFormDefaultValues } from '../utils/getEInvoicingAddressFormDefaultValues'
import { getEInvoicingAddressFormValidationSchema } from '../utils/getEInvoicingAddressFormValidationSchema'
import { normalizeAddressingLine } from '../utils/normalizeAddressingLine'

interface ContextState {
  close: () => void
  eInvoicingAddressForm: UseFormReturn<EInvoicingAddressFormSchema>
  fetchAddressingLines: () => Promise<AddressingLineLookupStatus | undefined>
  isLoading: boolean
  isLookupLoading: boolean
  isSubmitting: boolean
  isUnregisteredAddressingLineAcknowledged: boolean
  lookupStatus?: AddressingLineLookupStatus
  setIsUnregisteredAddressingLineAcknowledged: Dispatch<SetStateAction<boolean>>
  submitForm: () => void
  validate: () => Promise<boolean>
}

const EInvoicingAddressFormContext = createContext<ContextState | undefined>(undefined)

interface EInvoicingAddressFormContextProviderProps {
  children?: ReactNode
  isSubmitting: boolean
  modalId: string
  onCustomerUpdated?: (customer: Contact) => void
  onSubmit: (formData: SendAsEInvoiceForm) => void
  predefinedCustomerValues?: PredefinedCustomerValues
}

export const EInvoicingAddressFormContextProvider = ({
  children,
  isSubmitting: isSubmittingSendAsEInvoice,
  modalId,
  onCustomerUpdated,
  onSubmit,
  predefinedCustomerValues,
}: EInvoicingAddressFormContextProviderProps): ReactElement => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { close: closeSendAsEInvoiceModal } = useModal(modalId)
  const { attachmentUuids, hasFailedAttachments } = useAttachmentsUploadManager()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const [isUnregisteredAddressingLineAcknowledged, setIsUnregisteredAddressingLineAcknowledged] = useState(false)

  const eInvoicingAddressForm = useForm<EInvoicingAddressFormSchema>({
    defaultValues: getEInvoicingAddressFormDefaultValues(predefinedCustomerValues),
    validationSchema: getEInvoicingAddressFormValidationSchema(t, hasFailedAttachments, isFrenchComplianceMode),
  })

  const { handleSubmit, reset: resetForm, trigger, watch } = eInvoicingAddressForm

  const addressingLine = watch('addressingLine')

  const { fetchAddressingLines, isLookupLoading, lookupStatus } = useFetchEInvoicingAddressingLines(addressingLine)

  const handleUpdateCustomerOnSubmit = useCallback(
    (customer: Contact) => {
      queryClient.invalidateQueries([QueryKeys.ContactsAndContactPersons])
      onCustomerUpdated?.(customer)
    },
    [onCustomerUpdated, queryClient],
  )

  const handleSubmitFormSuccess = useCallback(
    (customer: Contact) => {
      handleUpdateCustomerOnSubmit(customer)

      const formData = {
        addressingLine: addressingLine ? normalizeAddressingLine(addressingLine) : addressingLine,
        attachmentUuids,
        ...customer,
      }
      onSubmit(formData)
      closeSendAsEInvoiceModal()
    },
    [attachmentUuids, addressingLine, closeSendAsEInvoiceModal, handleUpdateCustomerOnSubmit, onSubmit],
  )

  const {
    submit,
    isLoading,
    isSubmitting: isSubmittingCustomerForm,
  } = useUpdateCustomerInformation({ onSubmit: handleSubmitFormSuccess })

  const isSubmitting = isSubmittingSendAsEInvoice || isSubmittingCustomerForm

  useEffect(() => {
    if (predefinedCustomerValues) {
      const updatedEInvoicingAddressFormValues = getEInvoicingAddressFormDefaultValues(predefinedCustomerValues)
      resetForm(updatedEInvoicingAddressFormValues)
    }
  }, [predefinedCustomerValues, resetForm])

  // we're using a fake submit for validation due to how useForm's mode param works
  const handleValidation = useCallback(async () => {
    await handleSubmit(noop)()

    return trigger()
  }, [handleSubmit, trigger])

  const submitForm = useCallback(() => {
    handleSubmit(submit)()
  }, [handleSubmit, submit])

  const contextValue = useMemo(
    () => ({
      close: closeSendAsEInvoiceModal,
      eInvoicingAddressForm,
      fetchAddressingLines,
      isLoading,
      isLookupLoading,
      isSubmitting,
      isUnregisteredAddressingLineAcknowledged,
      lookupStatus,
      setIsUnregisteredAddressingLineAcknowledged,
      submitForm,
      validate: handleValidation,
    }),
    [
      closeSendAsEInvoiceModal,
      eInvoicingAddressForm,
      fetchAddressingLines,
      handleValidation,
      isLoading,
      isLookupLoading,
      isSubmitting,
      isUnregisteredAddressingLineAcknowledged,
      lookupStatus,
      setIsUnregisteredAddressingLineAcknowledged,
      submitForm,
    ],
  )

  return <EInvoicingAddressFormContext.Provider value={contextValue}>{children}</EInvoicingAddressFormContext.Provider>
}

export const useEInvoicingAddressForm = () => {
  const context = useContext(EInvoicingAddressFormContext)

  if (!context) {
    throw new Error('EInvoicingAddressFormContextProvider is missing in the module!')
  }

  return context
}
